import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstant from "../../../../../api";
import { isCvRunning } from "../../../../../types/consumerVoice";
import type { ConsumerVoiceAudit } from "../../../../../types/consumerVoice";
import { isSessionRedirect } from "../pageModel";
import { CV_POLL_MS, cvCurrent, cvItemUrl, cvListUrl, cvListValidateStatus, cvReadKey } from "./cvModel";
import type { CvItemState, CvLoad, CvTagged } from "./cvModel";

/**
 * Consumer Voice for one product — the section's reads. Read-only (v1): no
 * run, no hints, no settings; those stay in the settings modal.
 *
 * The page's own copy of the settings modal's reads (the modal is not edited,
 * D3): the same endpoints and query strings as ProductConsumerVoicePanel.tsx
 * and ConsumerVoiceHistorySection.tsx, each with its source line. What it adds
 * is what the page needs and the modal lacks: a load state that tells 404 /
 * 403 / failure apart (a 403 on the list without the global permission toast,
 * `cvListValidateStatus`), and the newest ready run's report (for the cards'
 * captions).
 *
 * `brandId` is `cvBrandIdForViewer(product.brands, scope.brandId,
 * isInternal(role))` — the modal's `cvBrandIdFor`, except a brand viewer's own
 * regular brand on the product wins (see cvModel). Null makes no call at all
 * (the na-network view).
 *
 * Stale responses are dropped with a sequence ref per call, as
 * MODAL/useProductAutopilot.ts:21-46 does; a product / brand switch and
 * unmount move every sequence on, so an old product's answer never writes into
 * the new one. Silent reads keep the last good data when they fail — and when
 * there is none yet (a silent read overtook the first load), their failure is
 * shown like a first load's. A failure that is the session ending
 * (`isSessionRedirect`: a 401, or the expired-token cancel) changes nothing:
 * the app is on its way to the login page, so no error block flashes first.
 *
 * Every read — the list, its load state, the report — is tagged with the
 * product and brand it was made for (`cvReadKey`), and the hook hands out
 * only the current key's (`cvCurrent`). In the render that switches the
 * product or brand, before the reset effect runs, the section therefore sees
 * a first load, never the old brand's cards.
 *
 * `reloadKey` (bumped when the settings modal closes) re-reads the list,
 * silently. The report is read again only when its load failed: a ready run's
 * report is written once, with the READY status (desirelist-api
 * lib/consumerVoice/audit.js:601-603; nothing else writes it), and a run that
 * lands ready meanwhile is a new `readyId`, whose report loads anyway.
 * `pollPaused` (the settings modal is open) stops the 8s poll; the reload on
 * close picks up whatever changed while it was open.
 */

export interface ProductConsumerVoice {
  /** The list, newest first (created_at DESC), up to 20 of any status. Null until the first read lands. */
  rows: ConsumerVoiceAudit[] | null;
  /** `latestReady(rows)`, worked out once here: the run whose cards the section shows. */
  ready: ConsumerVoiceAudit | null;
  load: CvLoad;
  /** Try again on the error strip: a full, non-silent list read. */
  retry: () => void;
  /**
   * The newest ready run with its report. A failure has no Retry button: the
   * cards fall back to the list row (closing Settings reads it again).
   */
  item: CvItemState;
}

export const useProductConsumerVoice = (
  desireid: number | null | undefined,
  brandId: number | null,
  reloadKey: number,
  pollPaused = false,
): ProductConsumerVoice => {
  // Each read, tagged with the product and brand it was made for.
  const [list, setList] = useState<CvTagged<ConsumerVoiceAudit[]> | null>(null);
  const [load, setLoad] = useState<CvTagged<CvLoad> | null>(null);
  const [item, setItem] = useState<CvTagged<CvItemState> | null>(null);

  // One sequence per call; a product / brand switch and unmount move both on.
  const listSeq = useRef(0);
  const itemSeq = useRef(0);

  // Only this product's and brand's reads (see cvCurrent).
  const current = cvCurrent(cvReadKey(desireid, brandId), !!desireid && !!brandId, { list, load, item });
  const { rows, ready } = current;
  // A list read for this key has landed. False in the commit that switches
  // the brand (the reset hasn't landed yet).
  const listFresh = rows != null;
  const listFreshRef = useRef(listFresh);
  listFreshRef.current = listFresh;

  const readyId = ready?.id ?? null;
  // The ready run's report failed to load (the call failed, or came back empty).
  const itemFailed = current.item.error;

  // ---- list: GET …/consumer-voice?brandId=&limit=20 --------------------------
  const loadList = useCallback(
    async (silent: boolean) => {
      if (!desireid || !brandId) return;
      const key = cvReadKey(desireid, brandId);
      listSeq.current += 1;
      const mine = listSeq.current;
      if (!silent) setLoad({ key, value: "loading" });
      const fail = (status: number | undefined) => {
        // Silent (the poll, a reload after the modal): keep the last good list.
        // With none yet — a silent read overtook the first load, whose answer
        // is dropped — this failure is the first load's, or the skeleton stays.
        if (silent && listFreshRef.current) return;
        setList(null);
        // 404 is ProductNotFound, the controller's only 404 here: the brand
        // isn't on the product (consumerVoice.js:343). 403: the viewer can't
        // read that brand (:332).
        if (status === 404) setLoad({ key, value: "noBrand" });
        else if (status === 403) setLoad({ key, value: "noAccess" });
        else setLoad({ key, value: "failed" });
      };
      try {
        // A 403 resolves here instead of rejecting, so the global permission
        // toast stays quiet (cvListValidateStatus); it is still a failure.
        const res = await axiosInstant.get(cvListUrl(desireid, brandId), { validateStatus: cvListValidateStatus });
        if (mine !== listSeq.current) return;
        if (res.status === 403) {
          fail(403);
          return;
        }
        const { data } = res;
        // An array today (consumerVoice.js:399-410); the history section also
        // reads {rows} (ConsumerVoiceHistorySection.tsx:55), so this does too.
        setList({ key, value: Array.isArray(data) ? data : (data && data.rows) || [] });
        // Already "loaded" for this key (every poll after the first): the same
        // state back, so React bails out of it (setList above re-rendered).
        setLoad((prev) => (prev && prev.key === key && prev.value === "loaded" ? prev : { key, value: "loaded" }));
      } catch (err: any) {
        if (mine !== listSeq.current) return;
        // The session is ending (a 401, or the expired-token cancel that never
        // left the browser, api/index.ts:78-81): the app is redirecting to
        // login — change nothing (the skeleton or the last list stays).
        if (isSessionRedirect(err)) return;
        fail(err?.response?.status);
      }
    },
    [desireid, brandId],
  );

  // ---- item: GET …/consumer-voice/:id?brandId= --------------------------------
  const loadItem = useCallback(
    async (id: number, silent: boolean) => {
      if (!desireid || !brandId) return;
      const key = cvReadKey(desireid, brandId);
      itemSeq.current += 1;
      const mine = itemSeq.current;
      if (!silent) setItem({ key, value: { id, audit: null, loading: true, error: false } });
      try {
        const { data } = await axiosInstant.get(cvItemUrl(desireid, id, brandId));
        if (mine !== itemSeq.current) return;
        setItem({ key, value: { id, audit: (data as ConsumerVoiceAudit) || null, loading: false, error: !data } });
      } catch (err: any) {
        if (mine !== itemSeq.current) return;
        if (isSessionRedirect(err)) return;
        // Silent: keep the report on screen unless there is none for this run.
        setItem((prev) =>
          silent && prev && prev.key === key && prev.value.id === id && prev.value.audit
            ? prev
            : { key, value: { id, audit: null, loading: false, error: true } },
        );
      }
    },
    [desireid, brandId],
  );

  // A product or brand switch starts over; unmount drops everything in flight.
  useEffect(() => {
    setList(null);
    setItem(null);
    if (!desireid || !brandId) setLoad({ key: cvReadKey(desireid, brandId), value: "noBrand" });
    else loadList(false);
    return () => {
      listSeq.current += 1;
      itemSeq.current += 1;
    };
  }, [desireid, brandId, loadList]);

  // The report follows the newest ready run: a run finishing swaps it. Only
  // for a list read for this brand: when the brand switches, `loadItem`
  // changes in the same commit while the old rows' readyId is still here,
  // and would GET the old run with the new brandId (the modal never does; a
  // 403 fires the global toast). The fresh list re-runs this.
  useEffect(() => {
    if (readyId == null || !listFresh) {
      itemSeq.current += 1;
      setItem(null);
      return;
    }
    loadItem(readyId, false);
  }, [readyId, listFresh, loadItem]);

  // The page bumps reloadKey when the settings modal closes (runs may have
  // started or finished there): re-read the list, silently. The report only
  // when it failed — a ready run's never changes (see the top). Only a
  // reloadKey change acts; the other deps just keep what it reads current.
  const seenReloadKey = useRef(reloadKey);
  useEffect(() => {
    if (reloadKey === seenReloadKey.current) return;
    seenReloadKey.current = reloadKey;
    loadList(true);
    if (itemFailed && readyId != null && listFresh) loadItem(readyId, true);
  }, [reloadKey, loadList, loadItem, itemFailed, readyId, listFresh]);

  // 8s silent re-read while any run is queued or running
  // (ConsumerVoiceHistorySection.tsx:16,71-76), so a first run's cards appear
  // when it finishes. A run past the 60-minute window is not polled. Paused
  // while the settings modal is open (`pollPaused`): its close re-reads.
  const polling = !!rows && rows.some((r) => isCvRunning(r));
  useEffect(() => {
    if (!polling || pollPaused) return undefined;
    const t = window.setInterval(() => loadList(true), CV_POLL_MS);
    return () => window.clearInterval(t);
  }, [polling, pollPaused, loadList]);

  const retry = useCallback(() => loadList(false), [loadList]);

  return { rows, ready, load: current.load, retry, item: current.item };
};
